export const FRAME_ROLES = {
  start: 'start',
  end: 'end'
}

export const FRAME_LABELS = {
  [FRAME_ROLES.start]: 'Start Frame',
  [FRAME_ROLES.end]: 'End Frame'
}

export const normalizeFrameUrl = value => {
  if (!value) return ''
  if (typeof value === 'string') return value
  return value.url || value.uri || value.image || value.src || ''
}

const normalizeFrame = (frame, fallbackUrl) => {
  const url = normalizeFrameUrl(frame) || fallbackUrl || ''
  if (!url) return null

  return {
    ...(typeof frame === 'object' && frame !== null ? frame : {}),
    url
  }
}

export const normalizeShotFrames = shot => {
  const frames = shot?.frames || {}
  const startFrame =
    normalizeFrame(frames.start, '') ||
    normalizeFrame(shot?.startFrame, '') ||
    normalizeFrame(shot?.firstFrame, '') ||
    normalizeFrame(shot?.start_frame, '') ||
    normalizeFrame(null, shot?.startFrameSrc || shot?.firstFrameSrc || '')

  const endFrame =
    normalizeFrame(frames.end, '') ||
    normalizeFrame(shot?.endFrame, '') ||
    normalizeFrame(shot?.lastFrame, '') ||
    normalizeFrame(shot?.end_frame, '') ||
    normalizeFrame(
      null,
      shot?.endFrameSrc || shot?.lastFrameSrc || shot?.last_frame_image || ''
    )

  return {
    start: startFrame || normalizeFrame(null, shot?.imageSrc || ''),
    end: endFrame
  }
}

export const getShotFrameUrl = (shot, role = FRAME_ROLES.start) => {
  const frames = normalizeShotFrames(shot)
  return normalizeFrameUrl(frames[role])
}

const createFrame = (url, previousFrame, source = 'manual') => {
  if (!url) return null

  return {
    ...(previousFrame || {}),
    url,
    source,
    updatedAt: Date.now()
  }
}

const appendHistoryEntries = (history, urls = []) => {
  const existing = Array.isArray(history) ? history : []
  const entries = urls
    .filter(Boolean)
    .filter(url => !existing.some(item => item?.url === url))
    .map(url => ({ url, timestamp: Date.now() }))

  return entries.length > 0 ? [...existing, ...entries] : existing
}

export const createShotFramesUpdate = (
  shot,
  nextFrames,
  options = {}
) => {
  const previousFrames = normalizeShotFrames(shot)
  const startUrl = normalizeFrameUrl(nextFrames?.start)
  const endUrl = normalizeFrameUrl(nextFrames?.end)
  const previousStartUrl = normalizeFrameUrl(previousFrames.start)
  const previousEndUrl = normalizeFrameUrl(previousFrames.end)
  const historyUrls = []

  if (startUrl && startUrl !== previousStartUrl) {
    historyUrls.push(startUrl)
  }

  if (endUrl && endUrl !== previousEndUrl) {
    historyUrls.push(endUrl)
  }

  const frames = {
    start: createFrame(
      startUrl,
      nextFrames?.start || previousFrames.start,
      options.source || 'manual'
    ),
    end: createFrame(
      endUrl,
      nextFrames?.end || previousFrames.end,
      options.source || 'manual'
    )
  }

  return {
    frames,
    imageSrc: startUrl,
    startFrameSrc: startUrl,
    endFrameSrc: endUrl,
    frame_mode: endUrl ? 'start_end' : 'single',
    images_history: appendHistoryEntries(shot?.images_history, historyUrls)
  }
}

export const createShotFrameUpdate = (
  shot,
  role,
  url,
  options = {}
) => {
  const frames = normalizeShotFrames(shot)

  return createShotFramesUpdate(
    shot,
    {
      ...frames,
      [role]: createFrame(url, frames[role], options.source || 'manual')
    },
    options
  )
}

export const removeShotFrame = (shot, role) => {
  const frames = normalizeShotFrames(shot)

  if (role === FRAME_ROLES.start) {
    return createShotFramesUpdate(shot, { ...frames, start: null })
  }

  return createShotFramesUpdate(shot, { ...frames, end: null })
}

export const swapShotFrames = shot => {
  const frames = normalizeShotFrames(shot)

  if (!frames.start || !frames.end) {
    return createShotFramesUpdate(shot, frames)
  }

  return createShotFramesUpdate(shot, {
    start: { ...frames.end, source: 'swap' },
    end: { ...frames.start, source: 'swap' }
  })
}

const getDisplayTitle = value => {
  const title = typeof value === 'string' ? value.trim() : ''
  return title || null
}

export const getShotFrameCopyTargets = (scenes = []) =>
  scenes.flatMap((scene, sceneIndex) =>
    (scene.shots || []).flatMap((shot, index) => {
      const frames = normalizeShotFrames(shot)
      const sceneNumber = sceneIndex + 1
      const shotNumber = index + 1
      const sceneTitle = getDisplayTitle(scene.title)
      const shotTitle = getDisplayTitle(shot.title)
      const sceneLabel = `Scene ${sceneNumber}`
      const shotLabel = `Shot ${shotNumber}${
        shotTitle ? `: ${shotTitle}` : ''
      }`

      return Object.values(FRAME_ROLES)
        .map(role => {
          const imageSrc = normalizeFrameUrl(frames[role])
          if (!imageSrc) return null

          return {
            value: `${scene.id}::${shot.id}::${role}`,
            sceneId: scene.id,
            shotId: shot.id,
            frameRole: role,
            imageSrc,
            sceneNumber,
            shotNumber,
            sceneTitle,
            shotTitle,
            sceneLabel,
            shotLabel,
            frameLabel: FRAME_LABELS[role],
            label: `${sceneLabel} - ${shotLabel} - ${FRAME_LABELS[role]}`
          }
        })
        .filter(Boolean)
    })
  )
